<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { adaptadorComEspacos } from 'dashboard/helper/markdownComEspacos';

const ACCEPTED_FILE_TYPES =
  'image/*, audio/*, video/*, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, text/plain';
const MAX_FILE_SIZE_MB = 40;

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      attachedFiles: [],
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    // O editor precisa devolver EXATAMENTE o texto que a pessoa salvou:
    // linhas em branco na conta certa e nenhuma barra inventada.
    adaptadorComEspacos: () => adaptadorComEspacos,
    acceptedFileTypes() {
      return ACCEPTED_FILE_TYPES;
    },
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachedFiles = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    onFileChange(event) {
      const files = Array.from(event.target.files);
      files.forEach(file => {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          useAlert(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
          return;
        }
        this.attachedFiles.push(file);
      });
      event.target.value = '';
    },
    removeFile(index) {
      this.attachedFiles.splice(index, 1);
    },
    getFileIcon(file) {
      if (file.type?.startsWith('image/')) return '🖼️';
      if (file.type?.startsWith('audio/')) return '🎵';
      if (file.type?.startsWith('video/')) return '🎬';
      return '📎';
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    addCannedResponse() {
      this.addCanned.showLoading = true;
      this.$store
        .dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
          files: this.attachedFiles,
        })
        .then(() => {
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              :markdown-adapter="adaptadorComEspacos"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @update:model-value="v$.content.$touch"
            />
          </div>
        </div>

        <!-- Attachments Section -->
        <div class="w-full mt-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CANNED_MGMT.ATTACHMENTS.LABEL') }}
          </label>
          <div class="flex flex-wrap gap-2 mt-1">
            <div
              v-for="(file, index) in attachedFiles"
              :key="index"
              class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-n-alpha-2 border border-n-weak text-xs"
            >
              <span>{{ getFileIcon(file) }}</span>
              <span class="max-w-[150px] truncate">{{ file.name }}</span>
              <span class="text-n-slate-10">{{
                formatFileSize(file.size)
              }}</span>
              <button
                type="button"
                class="ml-1 text-n-slate-10 hover:text-n-ruby-9"
                @click="removeFile(index)"
              >
                <fluent-icon icon="dismiss" size="12" />
              </button>
            </div>
          </div>
          <label
            class="inline-flex items-center gap-1.5 px-3 py-1.5 mt-2 text-xs font-medium rounded-lg cursor-pointer bg-n-alpha-2 border border-n-weak text-n-slate-12 hover:bg-n-alpha-3 transition-colors"
          >
            <fluent-icon icon="attach" size="14" />
            {{ $t('CANNED_MGMT.ATTACHMENTS.ADD') }}
            <input
              type="file"
              multiple
              :accept="acceptedFileTypes"
              class="hidden"
              @change="onFileChange"
            />
          </label>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              addCanned.showLoading
            "
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
